/**
 * Main application component for "Do I Need Sunscreen Right Now?"
 *
 * Orchestrates the entire app: fetches the user's geolocation, retrieves real-time
 * UV data from OpenWeatherMap, computes whether sunscreen is needed using the
 * MED-based algorithm in utils.js, and renders the full UI including sliders,
 * modals, and the UV forecast chart. Also manages rate limiting, premium/free
 * tier gating, cookie-based preference persistence, and dev-mode overrides.
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import Sun from "./Sun";
import NightSky from "./NightSky";
import PremiumModal from "./PremiumModal";
import PremiumFeatures from "./PremiumFeatures";
import LocationChangeModal from "./LocationChangeModal";
import TimeChangeModal from "./TimeChangeModal";
import DoctorBackedModal from "./DoctorBackedModal";
import SkinTypeQuizModal from "./SkinTypeQuizModal";
import BreakdownModal from "./BreakdownModal";
import { AuthProvider, useAuth } from "./AuthContext";
import {
  needsSunscreen,
  getBackgroundColor,
  getTextColor,
  formatDuration,
  getAppFeaturesBackgroundColor,
  getSafeExposureTime,
  SKIN_TYPE_DATA,
  CLOUD_COVER_FACTORS,
  SURFACE_FACTORS,
} from "./utils";
import {
  getManualUVIndex,
  getSimulatedHour,
  getForcedDarkness,
  logDevConfig,
  isPremiumVersionEnabled,
  isBreakdownEnabled,
} from "./dev-config";
import { trackPageLoad, trackEvent } from "./analytics";
import { isAfterDark } from "./solarCalculations";
import { loadUserPreferences, saveUserPreferences } from "./userPreferences";
import { trackAPICall, hasExceededRateLimit, migrateDailyUsersData } from "./apiTracking";

function App() {
  // Load user preferences on component initialization
  const userPrefs = loadUserPreferences();
  const { isPremium: realIsPremium } = useAuth();

  // Override premium status based on dev configuration
  // If premium version is disabled, all users get premium features
  const isPremium = isPremiumVersionEnabled() ? realIsPremium : true;

  const [uvIndex, setUvIndex] = useState(0);
  const [skinType, setSkinType] = useState(userPrefs.skinType);
  const [duration, setDuration] = useState(userPrefs.duration);
  const [otherFactors, setOtherFactors] = useState(userPrefs.otherFactors);
  const [loading, setLoading] = useState(true);
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [locationLoading, setLocationLoading] = useState(true);
  const [isDark, setIsDark] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [elevation, setElevation] = useState(null);
  const [isLocationChangeModalOpen, setIsLocationChangeModalOpen] =
    useState(false);
  const [isTimeChangeModalOpen, setIsTimeChangeModalOpen] = useState(false);
  const [fullHourlyData, setFullHourlyData] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null); // null means current time
  const [currentTimeData, setCurrentTimeData] = useState(null);
  const [graphMode, setGraphMode] = useState("now"); // 'now', 'today', 'tomorrow'
  const [graphOffset, setGraphOffset] = useState(0); // Hours offset for navigation
  const [cloudCover, setCloudCover] = useState(userPrefs.cloudCover || "clear"); // Cloud cover type
  const [surfaceType, setSurfaceType] = useState(
    userPrefs.surfaceType || "none"
  ); // Surface type for UV reflection
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [shareConfirmation, setShareConfirmation] = useState(null);

  // Ref to track if initial load has completed to prevent duplicate API calls
  const hasInitialized = useRef(false);

  // Check URL path on initial load to auto-open quiz modal
  useEffect(() => {
    if (window.location.pathname === '/quiz') {
      setIsQuizModalOpen(true);
      trackEvent('quizLoads');
    }
  }, []);

  // Get location name from coordinates using reverse geocoding
  const getLocationName = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
      );

      if (!response.ok) {
        throw new Error("Geocoding failed");
      }

      const data = await response.json();
      const city = data.city || data.locality || data.principalSubdivision;
      const state = data.principalSubdivisionCode || data.principalSubdivision;

      if (city && state) {
        setLocationName(`${city}, ${state}`);
      } else if (city) {
        setLocationName(city);
      } else {
        setLocationName("Location found");
      }
    } catch (error) {
      console.error("Error getting location name:", error);
      setLocationName("Location found");
    } finally {
      setLocationLoading(false);
    }
  };

  // Get elevation from coordinates using our Firebase function
  const getElevation = async (lat, lon) => {
    try {
      console.log("Fetching elevation for coordinates:", lat, lon);

      const response = await fetch(
        `https://us-central1-do-i-need-sunscreen.cloudfunctions.net/getElevation?lat=${lat}&lon=${lon}`
      );

      if (!response.ok) {
        throw new Error(`Elevation API failed: ${response.status}`);
      }

      const data = await response.json();
      console.log("Elevation API response:", data);

      if (data.success && data.elevation !== undefined) {
        console.log(`Setting elevation: ${data.elevation} ft`);
        setElevation(data.elevation);
      } else {
        console.warn("Elevation API returned no results:", data);
      }
    } catch (error) {
      console.error("Error getting elevation:", error);
    }
  };

  // Handle location change from modal
  const handleLocationChange = async (lat, lon, formattedAddress) => {
    trackEvent('locationChanges');
    try {
      setLoading(true);
      setLocationLoading(true);

      // Update coordinates
      setCoordinates({ latitude: lat, longitude: lon });

      // Update location name
      setLocationName(formattedAddress);

      // Check if it's currently dark at the new location
      const isDarkNow = isAfterDark(lat, lon);
      setIsDark(isDarkNow);

      // Fetch UV data and elevation for new location
      await Promise.all([
        fetchUVIndexForCoordinates(lat, lon),
        getElevation(lat, lon),
      ]);
    } catch (error) {
      console.error("Error updating location:", error);
    } finally {
      setLoading(false);
      setLocationLoading(false);
    }
  };

  // Extracted UV fetching logic to reuse for location changes
  const fetchUVIndexForCoordinates = async (lat, lon) => {
    try {
      // Check rate limit first (unless in manual UV mode or user is premium)
      const rateLimit = await hasExceededRateLimit(isPremium);

      if (rateLimit.exceeded) {
        console.log(
          `Rate limit exceeded: ${rateLimit.callCount}/50 API calls in 30 days`
        );
        setIsRateLimited(true);
        setRateLimitInfo(rateLimit);
        setIsPremiumModalOpen(true);

        // Set fallback values without making API call
        setUvIndex(5); // Default moderate UV
        setIsDark(false); // Default to daytime
        return;
      }

      const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

      if (!API_KEY) {
        console.warn(
          "OpenWeatherMap API key not found. Using time-based simulation."
        );

        // Use simulated hour if configured, otherwise use current time
        let currentTime = new Date();
        const simulatedHour = getSimulatedHour();

        if (simulatedHour !== null) {
          currentTime = new Date();
          currentTime.setHours(simulatedHour, 0, 0, 0);
          console.log(`🔧 Using simulated time: ${simulatedHour}:00`);
        }

        // Check if it's dark using accurate solar calculations
        const isDarkNow = isAfterDark(lat, lon, currentTime);
        setIsDark(isDarkNow);

        // Use simple time-based simulation
        const hour = currentTime.getHours();
        let simulatedUV;

        if (isDarkNow || hour < 6 || hour > 19) {
          simulatedUV = 0;
        } else if (hour < 8 || hour > 17) {
          simulatedUV = Math.random() * 3;
        } else if (hour < 10 || hour > 15) {
          simulatedUV = 3 + Math.random() * 4;
        } else {
          simulatedUV = 6 + Math.random() * 5;
        }

        setUvIndex(Math.round(simulatedUV));

        // Track simulated API call for analytics
        trackAPICall();
        return;
      }

      const response = await fetch(
        `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&exclude=minutely,daily,alerts&appid=${API_KEY}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("UV API response:", data);

      // Track API call for analytics
      trackAPICall();

      // Extract and set cloud cover
      if (data.current?.clouds !== undefined) {
        const clouds = data.current.clouds;

        // Determine cloud cover type based on percentage
        if (clouds <= 10) {
          setCloudCover("clear");
        } else if (clouds <= 50) {
          setCloudCover("scattered");
        } else if (clouds <= 84) {
          setCloudCover("broken");
        } else {
          setCloudCover("overcast");
        }
      }

      // Store current time data
      setCurrentTimeData({
        uvi: data.current?.uvi || 0,
        timestamp: Math.floor(Date.now() / 1000),
      });

      // Process hourly UV forecast data
      if (data.hourly) {
        // Store full hourly data for time selection
        setFullHourlyData(data.hourly);

        // Process data for current or selected time
        processTimeData(data.hourly, data.current);
      }

      // Set darkness state based on coordinates
      const isDarkNow = isAfterDark(lat, lon);
      setIsDark(isDarkNow);
    } catch (error) {
      console.error("Error fetching UV index:", error);
      // Fallback to moderate UV on API error
      setUvIndex(5);
      // Try to set darkness state based on coordinates if available
      if (coordinates) {
        const isDarkNow = isAfterDark(lat, lon);
        setIsDark(isDarkNow);
      }
    }
  };

  // Process time data for selected time or current time
  const processTimeData = useCallback(
    (hourlyData, currentData) => {
      // Find the data for the target time or use current data
      let targetUV;
      let targetDarkness;

      if (selectedTime) {
        // Find the closest hourly data to selected time
        const targetHour = hourlyData.find(
          (hour) => Math.abs(hour.dt - selectedTime) < 1800
        ); // Within 30 minutes
        targetUV = targetHour ? targetHour.uvi : 0;

        // Calculate darkness for selected time
        if (coordinates) {
          const selectedDate = new Date(selectedTime * 1000);
          targetDarkness = isAfterDark(
            coordinates.latitude,
            coordinates.longitude,
            selectedDate
          );
        } else {
          targetDarkness = false;
        }
      } else {
        // Use current data
        targetUV = currentData?.uvi || 0;
        targetDarkness = coordinates
          ? isAfterDark(coordinates.latitude, coordinates.longitude)
          : false;
      }

      setUvIndex(Math.round(targetUV));
      setIsDark(targetDarkness);
    },
    [selectedTime, coordinates]
  );

  // Handle time change from modal
  const handleTimeChange = async (newTimestamp) => {
    setSelectedTime(newTimestamp);
    trackEvent('timeChanges');
  };

  // Handle mode button clicks (Today/Now/Tomorrow) — update selected time
  const handleModeChange = (mode) => {
    if (mode === 'now') {
      setSelectedTime(null); // Reset to current time
    } else if (mode === 'today') {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayStartTs = Math.floor(todayStart.getTime() / 1000);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const todayEndTs = Math.floor(todayEnd.getTime() / 1000);
      const firstToday = fullHourlyData.find(
        (h) => h.dt >= todayStartTs && h.dt <= todayEndTs
      );
      if (firstToday) setSelectedTime(firstToday.dt);
    } else if (mode === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowSunrise = getSunTimes(tomorrow);
      setSelectedTime(tomorrowSunrise.sunrise || Math.floor(Date.now() / 1000) + 24 * 3600);
    }
  };

  // Calculate sunrise and sunset times (simple approximation)
  const getSunTimes = useCallback((date = new Date()) => {
    if (!coordinates) return { sunrise: null, sunset: null };

    const sunrise = new Date(date);
    sunrise.setHours(6, 0, 0, 0); // 6 AM approximation

    const sunset = new Date(date);
    sunset.setHours(18, 0, 0, 0); // 6 PM approximation

    return {
      sunrise: Math.floor(sunrise.getTime() / 1000),
      sunset: Math.floor(sunset.getTime() / 1000),
    };
  }, [coordinates]);

  // Generate forecast data based on mode and offset
  const getGraphForecast = useCallback(() => {
    if (!fullHourlyData || fullHourlyData.length === 0) return [];

    const currentTime = Math.floor(Date.now() / 1000);
    // Use selectedTime if available, otherwise use current time
    const effectiveTime = selectedTime || currentTime;
    const windowSize = 12; // Always 12 hours
    let baseStartTime;

    switch (graphMode) {
      case "now":
        // Use selectedTime or current time at start of 12-hour window
        baseStartTime = effectiveTime;
        break;

      case "today":
        // Today from first available data point today
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartTimestamp = Math.floor(todayStart.getTime() / 1000);

        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        const todayEndTimestamp = Math.floor(todayEnd.getTime() / 1000);

        // Find first data point for today
        const firstTodayDataPoint = fullHourlyData.find(
          (hour) =>
            hour.dt >= todayStartTimestamp && hour.dt <= todayEndTimestamp
        );

        baseStartTime = firstTodayDataPoint
          ? firstTodayDataPoint.dt
          : currentTime;
        break;

      case "tomorrow":
        // Tomorrow from sunrise
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowSunTimes = getSunTimes(tomorrow);
        baseStartTime = tomorrowSunTimes.sunrise || currentTime + 24 * 3600;
        break;

      default:
        baseStartTime = currentTime;
    }

    // Apply offset
    const startTime = baseStartTime + graphOffset * 3600;

    // Filter hourly data for the calculated window
    let windowData = fullHourlyData
      .filter((hour) => {
        return hour.dt >= startTime;
      })
      .slice(0, windowSize);

    // Ensure we have at least windowSize data points for 'today' mode
    if (
      graphMode === "today" &&
      windowData.length < windowSize &&
      fullHourlyData.length >= windowSize
    ) {
      // If not enough data from the calculated start time, find the earliest point that gives us 12 hours
      for (let i = 0; i <= fullHourlyData.length - windowSize; i++) {
        const potentialStart = fullHourlyData[i].dt;
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayStartTimestamp = Math.floor(todayStart.getTime() / 1000);

        // Only use data points from today onwards
        if (potentialStart >= todayStartTimestamp) {
          windowData = fullHourlyData.slice(i, i + windowSize);
          break;
        }
      }
    }

    const currentDate = new Date();
    const todayDateStr = currentDate.toLocaleDateString();

    return windowData.map((hour, index) => {
      const hourTime = new Date(hour.dt * 1000);
      const timeDiff = (hour.dt - currentTime) / 3600;
      const hourDateStr = hourTime.toLocaleDateString();
      const isToday = hourDateStr === todayDateStr;

      // For non-today dates, show date on first and last datapoints
      let timeLabel = hourTime.toLocaleTimeString("en-US", {
        hour: "numeric",
        hour12: true,
      });

      if (!isToday && (index === 0 || index === windowData.length - 1)) {
        const shortDate = hourTime.toLocaleDateString("en-US", {
          month: "numeric",
          day: "numeric",
        });
        timeLabel += ` ${shortDate}`;
      }

      // Check if this hour matches the selected or current time
      const timeDiffFromEffective = (hour.dt - effectiveTime) / 3600;

      return {
        time: timeLabel,
        uv: Math.round((hour.uvi || 0) * 10) / 10,
        timestamp: hour.dt,
        isPast: timeDiff < 0,
        isCurrent: graphMode === "now" && Math.abs(timeDiffFromEffective) < 0.5,
        hoursDiff: Math.round(timeDiff),
      };
    });
  }, [fullHourlyData, graphMode, graphOffset, selectedTime, getSunTimes]);

  // Get navigation constraints
  const getNavigationLimits = useCallback(() => {
    if (!fullHourlyData || fullHourlyData.length === 0) {
      return {
        canGoLeft: false,
        canGoRight: false,
        minOffset: 0,
        maxOffset: 0,
      };
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const windowSize = 12;
    let baseStartTime;

    switch (graphMode) {
      case "now":
        baseStartTime = currentTime;
        break;
      case "today":
        const todaySunTimes = getSunTimes();
        baseStartTime = todaySunTimes.sunrise;
        break;
      case "tomorrow":
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowSunTimes = getSunTimes(tomorrow);
        baseStartTime = tomorrowSunTimes.sunrise;
        break;
      default:
        baseStartTime = currentTime;
    }

    const firstDataTime = fullHourlyData[0]?.dt;
    const lastDataTime = fullHourlyData[fullHourlyData.length - 1]?.dt;

    // Calculate min/max offsets
    const minOffset = Math.ceil((firstDataTime - baseStartTime) / 3600);
    const maxOffset = Math.floor(
      (lastDataTime - baseStartTime - (windowSize - 1) * 3600) / 3600
    );

    return {
      canGoLeft: graphOffset > minOffset,
      canGoRight: graphOffset < maxOffset,
      minOffset,
      maxOffset,
    };
  }, [fullHourlyData, graphMode, graphOffset, getSunTimes]);

  // Get display time for the time line
  const getDisplayTime = () => {
    if (!selectedTime) {
      return new Date().toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    } else {
      const selectedDate = new Date(selectedTime * 1000);
      return selectedDate.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    }
  };

  // Get user's location and fetch UV index
  useEffect(() => {
    // Prevent duplicate execution (React StrictMode in development can cause double execution)
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Log dev configuration in development mode
    logDevConfig();

    // Increment daily page load counter
    trackPageLoad();

    // Run one-time migration for daily users data (converts old format to new)
    migrateDailyUsersData();

    // Check if manual UV override is enabled (highest priority)
    const manualUV = getManualUVIndex();
    if (manualUV !== null) {
      console.log(`🔧 Using manual UV index: ${manualUV}`);
      setUvIndex(manualUV);

      // Check for forced darkness override, otherwise use UV 0 as dark
      const forcedDarkness = getForcedDarkness();
      if (forcedDarkness !== null) {
        setIsDark(forcedDarkness);
        console.log(`🔧 Using forced darkness: ${forcedDarkness}`);
      } else {
        setIsDark(manualUV === 0);
      }

      setLoading(false);
      setLocationLoading(false);
      setLocationName("Dev Mode");
      return;
    }

    const fetchUVIndex = async (lat, lon) => {
      try {
        // Store coordinates for solar calculations
        setCoordinates({ latitude: lat, longitude: lon });

        // Check rate limit first (unless in manual UV mode or user is premium)
        const rateLimit = await hasExceededRateLimit(isPremium);

        if (rateLimit.exceeded) {
          console.log(
            `Rate limit exceeded: ${rateLimit.callCount}/50 API calls in 30 days`
          );
          setIsRateLimited(true);
          setRateLimitInfo(rateLimit);
          setIsPremiumModalOpen(true); // Show the rate limit modal immediately

          // Set fallback values without making API call
          setUvIndex(5); // Default moderate UV
          setIsDark(false); // Default to daytime
          return;
        }

        const API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

        if (!API_KEY) {
          console.warn(
            "OpenWeatherMap API key not found. Using time-based simulation."
          );

          // Use simulated hour if configured, otherwise use current time
          let currentTime = new Date();
          const simulatedHour = getSimulatedHour();

          if (simulatedHour !== null) {
            currentTime = new Date();
            currentTime.setHours(simulatedHour, 0, 0, 0);
            console.log(`🔧 Using simulated time: ${simulatedHour}:00`);
          }

          // Check if it's dark using accurate solar calculations
          const isDarkNow = isAfterDark(lat, lon, currentTime);
          setIsDark(isDarkNow);

          // Use simple time-based simulation instead of complex solar calculations
          const hour = currentTime.getHours();
          let simulatedUV;

          if (isDarkNow || hour < 6 || hour > 19) {
            simulatedUV = 0;
          } else if (hour < 8 || hour > 17) {
            simulatedUV = Math.random() * 3;
          } else if (hour < 10 || hour > 15) {
            simulatedUV = 3 + Math.random() * 4;
          } else {
            simulatedUV = 6 + Math.random() * 5;
          }

          setUvIndex(Math.round(simulatedUV));

          // Track simulated API call for analytics
          trackAPICall();
          return;
        }

        const response = await fetch(
          `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&exclude=minutely,daily,alerts&appid=${API_KEY}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("UV API response:", data);

        // Track API call for analytics
        trackAPICall();

        // Extract and set cloud cover
        if (data.current?.clouds !== undefined) {
          const clouds = data.current.clouds;

          // Determine cloud cover type based on percentage
          if (clouds <= 10) {
            setCloudCover("clear");
          } else if (clouds <= 50) {
            setCloudCover("scattered");
          } else if (clouds <= 84) {
            setCloudCover("broken");
          } else {
            setCloudCover("overcast");
          }
        }

        // Store current time data
        setCurrentTimeData({
          uvi: data.current?.uvi || 0,
          timestamp: Math.floor(Date.now() / 1000),
        });

        // Process hourly UV forecast data
        if (data.hourly) {
          // Store full hourly data for time selection
          setFullHourlyData(data.hourly);

          // Process data for current or selected time
          processTimeData(data.hourly, data.current);
        }

        // Set darkness state based on coordinates
        const isDarkNow = isAfterDark(lat, lon);
        setIsDark(isDarkNow);
      } catch (error) {
        console.error("Error fetching UV index:", error);
        // Fallback to moderate UV on API error
        setUvIndex(5);
        // Try to set darkness state based on coordinates if available
        if (coordinates) {
          const isDarkNow = isAfterDark(
            coordinates.latitude,
            coordinates.longitude
          );
          setIsDark(isDarkNow);
        }
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Set coordinates for darkness calculations
          setCoordinates({ latitude, longitude });

          // Check if it's currently dark
          const isDarkNow = isAfterDark(latitude, longitude);
          setIsDark(isDarkNow);

          // Fetch UV data, location name, and elevation in parallel
          await Promise.all([
            fetchUVIndex(latitude, longitude),
            getLocationName(latitude, longitude),
            getElevation(latitude, longitude),
          ]);
          setLoading(false);
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Keep loading state and show location request message
          setLocationName("Location access required");
          setLocationLoading(false);
          // Don't set loading to false - keep the loading screen visible
        }
      );
    } else {
      console.warn("Geolocation not supported");
      // Keep loading state and show geolocation not supported message
      setLocationName("Geolocation not supported");
      setLocationLoading(false);
      // Don't set loading to false - keep the loading screen visible
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodically refresh UV data and re-fetch when the user returns to the tab
  useEffect(() => {
    // Skip if no coordinates yet or using manual UV override
    if (!coordinates || getManualUVIndex() !== null) return;

    const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
    const MIN_HIDDEN_MS = 2 * 60 * 1000; // only re-fetch if tab was hidden 2+ min
    let hiddenAt = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt >= MIN_HIDDEN_MS) {
        hiddenAt = null;
        fetchUVIndexForCoordinates(coordinates.latitude, coordinates.longitude);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = setInterval(() => {
      // Only refresh if the tab is visible
      if (!document.hidden) {
        fetchUVIndexForCoordinates(coordinates.latitude, coordinates.longitude);
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordinates]);

  // Save user preferences when skin type, duration, surfaceType, cloudCover, or otherFactors change
  useEffect(() => {
    const preferences = {
      skinType,
      duration,
      surfaceType,
      cloudCover,
      otherFactors,
    };
    saveUserPreferences(preferences);
  }, [skinType, duration, surfaceType, cloudCover, otherFactors]);

  // Reprocess data when selectedTime changes
  useEffect(() => {
    if (fullHourlyData.length > 0 && currentTimeData) {
      processTimeData(fullHourlyData, currentTimeData);
    }
  }, [selectedTime, fullHourlyData, currentTimeData, coordinates, processTimeData]);

  // Calculate reapplication time for premium users
  const getReapplicationTime = () => {
    // Get safe exposure time using MED-based calculation
    const safeTime = getSafeExposureTime(
      uvIndex,
      skinType,
      cloudCover,
      surfaceType,
      elevation || 0,
      otherFactors
    );

    // if (!safeTime) {
    //   return "No reapplication needed"; // UV is 0
    // }

    // Debug logging
    console.log("Reapplication calculation:", {
      uvIndex,
      cloudCover,
      surfaceType,
      elevation,
      skinType,
      safeTime,
    });

    // Round down to the nearest minute for display only
    const displayTime = Math.floor(safeTime);
    const hours = Math.floor(displayTime / 60);
    const minutes = displayTime % 60;

    const timeString = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    // Debug final calculation
    console.log("Final reapplication time:", {
      safeTime,
      baseTimeFloat: safeTime,
      displayTime,
      timeString,
    });

    return `Apply within ${timeString} to avoid burn`;
  };

  const handleShare = async (perspective = 'me') => {
    const emoji = isDark ? '\uD83C\uDF19' : '\u2600\uFE0F';
    const subject = perspective === 'me' ? 'I' : 'You';
    const isFuture = selectedTime && selectedTime > Math.floor(Date.now() / 1000);
    let verb;
    let timeLabel = '';
    if (isFuture) {
      const futureDate = new Date(selectedTime * 1000);
      const now = new Date();
      const timeStr = futureDate.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
      if (futureDate.toDateString() !== now.toDateString()) {
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dayStr = futureDate.toDateString() === tomorrow.toDateString()
          ? 'tomorrow'
          : futureDate.toLocaleDateString('en-US', { weekday: 'long' });
        timeLabel = ` at ${timeStr} ${dayStr}`;
      } else {
        timeLabel = ` at ${timeStr}`;
      }
      verb = recommendation === 'YES'
        ? `${subject}'ll need sunscreen${timeLabel}!`
        : `${subject} won't need sunscreen${timeLabel}!`;
    } else {
      verb = recommendation === 'YES'
        ? `${subject} need sunscreen right now!`
        : `${subject} don't need sunscreen right now!`;
    }
    const skinLabel = SKIN_TYPE_DATA[skinType]?.label || `Type ${skinType}`;
    const cloudLabel = cloudCover === 'clear' ? 'Clear' :
      cloudCover === 'scattered' ? 'Scattered clouds' :
      cloudCover === 'broken' ? 'Broken clouds' :
      cloudCover === 'overcast' ? 'Overcast' : cloudCover;

    let snippet = `${emoji} ${verb}\n\n`;
    snippet += `Skin: ${skinLabel} \u00B7 UV: ${uvIndex} \u00B7 ${cloudLabel}\n`;
    snippet += `\uD83D\uDCCD ${locationName}`;
    if (elevation) snippet += ` \u00B7 ${elevation.toLocaleString()} ft`;
    snippet += `\n\nhttps://sunscreen.fyi/`;

    trackEvent('shareClicks');

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({ text: snippet });
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(snippet);
      setShareConfirmation(perspective);
      setTimeout(() => setShareConfirmation(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };


  if (loading) {
    return (
      <div className="App loading">
        <div>
          {locationLoading ? (
            "Getting your location and UV data..."
          ) : locationName === "Location access required" ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <h2 style={{ marginBottom: "20px" }}>
                📍 Location Access Required
              </h2>
              <p style={{ marginBottom: "15px" }}>
                This app needs your location to provide accurate UV index data
                for your area.
              </p>
              <p style={{ marginBottom: "15px" }}>
                You need to enable location sharing in your browser settings.
              </p>
              <p style={{ fontSize: "14px", opacity: "0.7" }}>
                On iOS: Settings / Privacy & Security / Location Services.
              </p>
              <div style={{ marginTop: "28px" }}>
                <p style={{ marginBottom: "10px", fontWeight: 600 }}>
                  Get the app and lock screen widget
                </p>
                <a
                  href="https://apps.apple.com/us/app/do-i-need-sunscreen-right-now/id6759970432"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-badge"
                >
                  <img
                    src="/app-store-badge.svg"
                    alt="Download on the App Store"
                    height="40"
                  />
                </a>
              </div>
            </div>
          ) : locationName === "Geolocation not supported" ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <h2 style={{ marginBottom: "20px" }}>
                ⚠️ Geolocation Not Supported
              </h2>
              <p style={{ marginBottom: "15px" }}>
                Your browser doesn't support location services.
              </p>
              <p style={{ fontSize: "14px", opacity: "0.7" }}>
                Please try using a modern browser like Chrome, Firefox, or
                Safari.
              </p>
            </div>
          ) : (
            "Getting your location and UV data..."
          )}
        </div>
      </div>
    );
  }

  // Build hourly UV data for dose integration
  let hourlyUVForCalculation;

  if (fullHourlyData.length > 0) {
    // Determine the start time: use selected time if available, otherwise current time
    const startTime =
      selectedTime ||
      currentTimeData?.timestamp ||
      Math.floor(Date.now() / 1000);

    // Find the index in fullHourlyData that matches the start time (within 30 minutes)
    let startIndex = fullHourlyData.findIndex(
      (h) => Math.abs(h.dt - startTime) < 1800
    );

    // If no exact match found, find the closest time after the start time
    if (startIndex === -1) {
      startIndex = fullHourlyData.findIndex((h) => h.dt >= startTime);
      // If still not found, use the first available data
      if (startIndex === -1) {
        startIndex = 0;
      }
    }

    // Calculate how many hours of data we need
    const hoursNeeded = Math.ceil(duration / 60);

    // Slice the data from the start index for the duration
    const availableData = fullHourlyData.slice(
      startIndex,
      startIndex + hoursNeeded
    );

    // Map to UV values, padding with zeros if not enough data
    hourlyUVForCalculation = availableData.map((h) => h.uvi || 0);

    // If we don't have enough hours of data, pad with the last known UV value or 0
    while (hourlyUVForCalculation.length < hoursNeeded) {
      const lastValue =
        hourlyUVForCalculation.length > 0
          ? hourlyUVForCalculation[hourlyUVForCalculation.length - 1]
          : 0;
      hourlyUVForCalculation.push(lastValue);
    }
  } else {
    // Fallback to single UV value when no hourly data available
    hourlyUVForCalculation = [uvIndex];
  }

  const recommendation = needsSunscreen(
    hourlyUVForCalculation,
    skinType,
    duration,
    cloudCover,
    surfaceType,
    elevation || 0,
    otherFactors
  )
    ? "YES"
    : "NO";

  return (
    <div
      className="App"
      style={{
        backgroundColor: getBackgroundColor(uvIndex, isDark),
        color: getTextColor(uvIndex, isDark),
        minHeight: "100vh",
      }}
    >
      <a href="https://johngrandolph.com" target="_blank" rel="noopener noreferrer" className="site-logo-link">
        <img src="/logo3.png" alt="Logo" className="site-logo" />
      </a>
      {isDark ? <NightSky /> : <Sun uvIndex={uvIndex} />}
      <div className="container">
        <h1 className="title">do I need sunscreen right now?</h1>

        <div className="recommendation">{recommendation}</div>

        {/* Reapplication Timer - always reserve space, show content only when needed */}
        <div className="reapplication-section">
          {recommendation === "YES" && (
            <>
              {isPremium ? (
                <div
                  className="reapplication-time"
                  style={{ color: getTextColor(uvIndex, isDark) }}
                >
                  {getReapplicationTime()}
                </div>
              ) : (
                <div
                  className="reapplication-locked"
                  style={{ color: getTextColor(uvIndex, isDark), opacity: 0.7 }}
                >
                  🔒 Upgrade to Premium for reapplication timing
                </div>
              )}
            </>
          )}
        </div>

        <div className="uv-info">
          UV Index: {uvIndex}
          <div className="location-info">
            {locationLoading ? (
              <em>Getting location...</em>
            ) : (
              <span>
                <em>{locationName}</em>
                {locationName && locationName !== "Dev Mode" && (
                  <span style={{ color: getTextColor(uvIndex, isDark) }}>
                    {" ("}
                    <button
                      className="change-location-link"
                      onClick={() => {
                        if (isPremium) {
                          setIsLocationChangeModalOpen(true);
                        } else {
                          setIsPremiumModalOpen(true);
                        }
                      }}
                    >
                      change
                    </button>
                    {")"}
                  </span>
                )}
              </span>
            )}
          </div>
          {elevation !== null && (
            <div className="elevation-info">
              <em>Elevation: {elevation.toLocaleString()} ft</em>
            </div>
          )}
          <div className="time-info">
            <em>{getDisplayTime()}</em>
            {fullHourlyData && fullHourlyData.length > 0 && (
              <span style={{ color: getTextColor(uvIndex, isDark) }}>
                {" ("}
                <button
                  className="change-time-link"
                  onClick={() => {
                    if (isPremium) {
                      setIsTimeChangeModalOpen(true);
                    } else {
                      setIsPremiumModalOpen(true);
                    }
                  }}
                >
                  change
                </button>
                {isPremium && selectedTime && (
                  <>
                    {" | "}
                    <button
                      className="change-time-link"
                      onClick={() => setSelectedTime(null)}
                    >
                      reset
                    </button>
                  </>
                )}
                {")"}
              </span>
            )}
          </div>
          {isBreakdownEnabled() && (
            <button
              className="breakdown-link"
              style={{ color: getTextColor(uvIndex, isDark) }}
              onClick={() => { setIsBreakdownModalOpen(true); trackEvent('breakdownClicks'); }}
            >
              breakdown
            </button>
          )}
        </div>

        <div
          className="controls"
          style={{ backgroundColor: SKIN_TYPE_DATA[skinType].color }}
        >
          <div className="slider-group">
            <label>
              Skin {SKIN_TYPE_DATA[skinType].label}{" "}
              <button
                className="fitzpatrick-link"
                onClick={() => { setIsQuizModalOpen(true); trackEvent('quizLoads'); }}
              >
                What's my skin type?
              </button>
              <div className="skin-type-description">
                {SKIN_TYPE_DATA[skinType].description}
              </div>
              <input
                type="range"
                min="1"
                max="6"
                value={skinType}
                onChange={(e) => setSkinType(parseInt(e.target.value))}
                className="slider"
              />
            </label>
          </div>

          <div className="slider-group">
            <label>
              Time Outdoors: {formatDuration(duration)}
              <input
                type="range"
                min="5"
                max="480"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="slider"
              />
            </label>
          </div>

          {/* Cloud Cover Control */}
          <div className="slider-group">
            <label>
              Cloud Cover
              <div className="button-group" style={{ marginTop: "10px" }}>
                {Object.entries(CLOUD_COVER_FACTORS).map(([key, value]) => (
                  <button
                    key={key}
                    className={`toggle-button ${
                      cloudCover === key ? "active" : ""
                    }`}
                    onClick={() => setCloudCover(key)}
                    style={{
                      margin: "5px",
                      padding: "8px 15px",
                      backgroundColor:
                        cloudCover === key ? "#FFD700" : "#f0f0f0",
                      color: cloudCover === key ? "white" : "black",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    {key === "scattered"
                      ? "Scattered Clouds"
                      : key === "broken"
                      ? "Broken Clouds"
                      : key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {/* Surface Type Control */}
          <div className="slider-group">
            <label>
              Surface
              <div
                className="button-group"
                style={{ marginTop: "10px" }}
              >
                {Object.entries(SURFACE_FACTORS).map(([key, value]) => (
                  <button
                    key={key}
                    className={`toggle-button ${
                      surfaceType === key ? "active" : ""
                    }`}
                    onClick={() => setSurfaceType(key)}
                    style={{
                      margin: "5px",
                      padding: "8px 15px",
                      backgroundColor:
                        surfaceType === key ? "#FFD700" : "#f0f0f0",
                      color: surfaceType === key ? "white" : "black",
                      border: "none",
                      borderRadius: "5px",
                      cursor: "pointer",
                    }}
                  >
                    {key === "none"
                      ? "None"
                      : key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
              </div>
            </label>
          </div>

          {/* Premium Controls */}
          <PremiumFeatures
            uvForecast={getGraphForecast()}
            skinType={skinType}
            duration={duration}
            uvIndex={uvIndex}
            elevation={elevation || 0}
            otherFactors={otherFactors}
            setOtherFactors={setOtherFactors}
            graphMode={graphMode}
            setGraphMode={setGraphMode}
            graphOffset={graphOffset}
            setGraphOffset={setGraphOffset}
            navigationLimits={getNavigationLimits()}
            onTimeSelect={handleTimeChange}
            onModeChange={handleModeChange}
            selectedTimestamp={selectedTime}
            isDark={isDark}
          />
        </div>

        {/* App Features Section */}
        <div
          className="app-features-section"
          style={{
            backgroundColor: getAppFeaturesBackgroundColor(uvIndex, isDark),
          }}
        >
          {/* App + Lock Screen Widget */}
          <div className={`app-feature ${!isPremium ? "locked" : ""}`}>
            <div className="app-feature-header">
              <label>App + lock screen widget</label>
            </div>
            {isPremium ? (
              <a
                href="https://apps.apple.com/us/app/do-i-need-sunscreen-right-now/id6759970432"
                target="_blank"
                rel="noopener noreferrer"
                className="app-store-badge"
              >
                <img
                  src="/app-store-badge.svg"
                  alt="Download on the App Store"
                  height="40"
                />
              </a>
            ) : (
              <div className="locked-message">
                <span className="lock-icon">🔒</span>
                <span>Upgrade to Premium for native app access</span>
              </div>
            )}
          </div>

          {/* Skin Type Quiz */}
          <div className="app-feature">
            <div className="app-feature-header">
              <label>What skin type am I?</label>
            </div>
            <button
              className="app-feature-button"
              onClick={() => { setIsQuizModalOpen(true); trackEvent('quizLoads'); }}
            >
              Take quiz
            </button>
          </div>

          {/* Share Result */}
          <div className="app-feature">
            <div className="app-feature-header">
              <label>Share result</label>
            </div>
            <div className="share-buttons">
              <button
                className={`app-feature-button ${shareConfirmation === 'me' ? 'copied' : ''}`}
                onClick={() => handleShare('me')}
              >
                {shareConfirmation === 'me' ? 'Copied!' : 'Me'}
              </button>
              <button
                className={`app-feature-button ${shareConfirmation === 'you' ? 'copied' : ''}`}
                onClick={() => handleShare('you')}
              >
                {shareConfirmation === 'you' ? 'Copied!' : 'You'}
              </button>
            </div>
          </div>
        </div>

        {!isPremium && isPremiumVersionEnabled() && (
          <button
            className="premium-button"
            onClick={() => setIsPremiumModalOpen(true)}
          >
            DoINeedSunscreenRightNow+
          </button>
        )}

        <button
          className="doctor-backed-button"
          onClick={() => { setIsDoctorModalOpen(true); trackEvent('doctorBackedClicks'); }}
        >
          Backed by doctors
        </button>

        <div className="site-footer">
          <span>sunscreen.fyi &copy; 2026</span>
          <span className="footer-separator">&middot;</span>
          <button
            className="feedback-link"
            onClick={() => setIsFeedbackModalOpen(true)}
          >
            feedback
          </button>
          <span className="footer-separator">&middot;</span>
          <a
            href="https://x.com/sunscreenNYC"
            target="_blank"
            rel="noopener noreferrer"
            className="twitter-link"
            aria-label="Follow on X"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <span className="footer-separator">&middot;</span>
          <a
            href="https://johngrandolph.com/html5up-forty/projects.html"
            target="_blank"
            rel="noopener noreferrer"
            className="feedback-link"
          >
            my projects
          </a>
          <span className="footer-separator">&middot;</span>
          <a href="/privacy" className="feedback-link">
            Privacy Policy
          </a>
        </div>
      </div>

      <PremiumModal
        isOpen={isPremiumModalOpen}
        onClose={() => {
          setIsPremiumModalOpen(false);
          // Reset rate limit state when modal closes
          setIsRateLimited(false);
          setRateLimitInfo(null);
        }}
        isRateLimited={isRateLimited}
        rateLimitInfo={rateLimitInfo}
      />

      <LocationChangeModal
        isOpen={isLocationChangeModalOpen}
        onClose={() => setIsLocationChangeModalOpen(false)}
        onLocationChange={handleLocationChange}
        uvIndex={uvIndex}
        isDark={isDark}
      />

      <TimeChangeModal
        isOpen={isTimeChangeModalOpen}
        onClose={() => setIsTimeChangeModalOpen(false)}
        onTimeChange={handleTimeChange}
        onModeChange={handleModeChange}
        isDark={isDark}
        uvForecast={getGraphForecast()}
        graphMode={graphMode}
        setGraphMode={setGraphMode}
        graphOffset={graphOffset}
        setGraphOffset={setGraphOffset}
        navigationLimits={getNavigationLimits()}
        selectedTimestamp={selectedTime}
      />

      <DoctorBackedModal
        isOpen={isDoctorModalOpen}
        onClose={() => setIsDoctorModalOpen(false)}
        uvIndex={uvIndex}
        isDark={isDark}
      />

      <BreakdownModal
        isOpen={isBreakdownModalOpen}
        onClose={() => setIsBreakdownModalOpen(false)}
        isDark={isDark}
        skinType={skinType}
        uvIndex={uvIndex}
        cloudCover={cloudCover}
        surfaceType={surfaceType}
        elevation={elevation || 0}
        otherFactors={otherFactors}
        safeExposureTime={getSafeExposureTime(
          uvIndex,
          skinType,
          cloudCover,
          surfaceType,
          elevation || 0,
          otherFactors
        )}
      />

      <SkinTypeQuizModal
        isOpen={isQuizModalOpen}
        onClose={() => {
          setIsQuizModalOpen(false);
          if (window.location.pathname === '/quiz') {
            window.history.replaceState({}, '', '/');
          }
        }}
        onComplete={(type) => {
          setSkinType(type);
          setIsQuizModalOpen(false);
          trackEvent('quizCompletions');
          if (window.location.pathname === '/quiz') {
            window.history.replaceState({}, '', '/');
          }
        }}
        isDark={isDark}
      />

      {isFeedbackModalOpen && (
        <div className="feedback-overlay" onClick={() => setIsFeedbackModalOpen(false)}>
          <div
            className="feedback-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: isDark ? '#2a2a2a' : '#ffffff',
              color: isDark ? '#ffffff' : '#000000'
            }}
          >
            <h3>Send feedback</h3>
            <textarea
              className="feedback-textarea"
              placeholder="Tell us what you think..."
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              rows={5}
              style={{
                backgroundColor: isDark ? '#1a1a1a' : '#f5f5f5',
                color: isDark ? '#ffffff' : '#000000',
                borderColor: isDark ? '#444' : '#ddd'
              }}
            />
            <div className="feedback-actions">
              <button
                className="feedback-cancel-btn"
                onClick={() => { setIsFeedbackModalOpen(false); setFeedbackText(''); }}
              >
                Cancel
              </button>
              <button
                className="feedback-submit-btn"
                disabled={!feedbackText.trim()}
                onClick={() => {
                  const subject = encodeURIComponent('Sunscreen.fyi Feedback');
                  const body = encodeURIComponent(feedbackText);
                  window.open(`mailto:john_randolph@alumni.brown.edu?subject=${subject}&body=${body}`);
                  setFeedbackText('');
                  setIsFeedbackModalOpen(false);
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const AppWithAuth = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithAuth;
